import { VideoBroadcastWidgetState } from '@repo/go-widgets';
import type { VideoBroadcastInitParams, VideoBroadcastStateParams } from '@repo/go-widgets';
import { action, comparer, makeObservable, observable, reaction } from 'mobx';

import { lagacyAgent } from '../../../../api/legacyAgent';
import { wellLogsWidgetAgent } from '../../../../api/wellLogsWidgetAgent';
import type { TCreateWidgetOptions, TGroup, TWellListCombinedData } from '../../../../entities/tab/TabEntity';
import { requireService } from '../../../../packages/di';
import { setWellIdInGroup } from '../../utils/setWellIdInGroup';
import type { GroupSelectStore } from '../group-select/GroupSelect.store';
import { getCorrectLocale } from '../operational-parameters-widget/OperationalParametersWidget.utils';
import { getLinkedVideoTimeCoordinator } from '../wellLogs-video-link/LinkedVideoTimeCoordinator';

import type { VideobroadcastWidgetEntity } from './VideobroadcastWidget.entity';

export class VideobroadcastWidgetStore {
  private readonly videobroadcastEntity: VideobroadcastWidgetEntity;
  groupSelectStore: GroupSelectStore;
  private readonly initParams: VideoBroadcastInitParams; //todo
  @observable isLoading: boolean = false;
  @observable isReady: boolean = false;
  @observable hasError: boolean = false;
  @observable isGroupSelectOpen: boolean = false;

  onCreateVideobroadcastWidget: (
    wellId: number | null,
    stateParams: Partial<VideoBroadcastStateParams> | null,
    options?: TCreateWidgetOptions,
  ) => void;

  constructor(
    videobroadcastEntity: VideobroadcastWidgetEntity,
    groupSelectStore: GroupSelectStore,
    onCreateVideobroadcastWidget: (
      wellId: number | null,
      stateParams: Partial<VideoBroadcastStateParams> | null,
      options?: TCreateWidgetOptions,
    ) => void,
    private readonly wellListPanel: TWellListCombinedData,
    private readonly theme = requireService('theme'),
    private readonly stompCachePublisherService = requireService('stompCachePublisherService'),
    private readonly wellStatusService = requireService('wellStatusService'),
    private readonly notifications = requireService('notifications'),
    private readonly language = requireService('language'),
    private readonly userPreferencesService = requireService('userPreference'),
  ) {
    this.onCreateVideobroadcastWidget = onCreateVideobroadcastWidget;
    const { wellIndexType } = videobroadcastEntity;
    this.videobroadcastEntity = videobroadcastEntity;
    this.groupSelectStore = groupSelectStore;

    this.initParams = {
      index: wellIndexType,
      legacyAxiosInstance: lagacyAgent,
      stompPublisher: this.stompCachePublisherService.stompPublisher,
      wellDirectoryServiceStompPublisher: this.wellStatusService,
      axiosInstance: wellLogsWidgetAgent,
    };

    makeObservable(this);
  }
  @action.bound
  exitFullScreen() {
    this.videobroadcastEntity.setFullscreen(false);
  }

  @action.bound
  setIsGroupSelectOpen(isOpen: boolean): void {
    this.isGroupSelectOpen = isOpen;
  }

  @action.bound
  setGroupForWellLogs(group: TGroup | null): void {
    this.videobroadcastEntity.setGroup(group ? group.id : null);
  }

  @action.bound
  setWellId(well: number | null): void {
    this.videobroadcastEntity.setWellId(well);
  }

  @action.bound
  setGroupSelectStore(groupSelectStore: GroupSelectStore) {
    this.groupSelectStore = groupSelectStore;
  }

  @action.bound
  private setReady(value: boolean): void {
    this.isReady = value;
  }

  @action.bound
  private setInActiveTab(value: boolean) {
    this.videobroadcastEntity.setInActiveTab(value);
  }

  private getNewState(wellId: number): VideoBroadcastWidgetState {
    return new VideoBroadcastWidgetState(
      this.initParams,
      {
        // TODO: remove hardcode
        wellId,
        width: 500,
        height: 500,
        locale: getCorrectLocale(this.language.language),
        theme: this.theme.theme,
        active: this.videobroadcastEntity.inActiveTab,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
        defaultSelectWellbore: this.userPreferencesService.preferences.autoSelectWellboreForTime,
      },
      this.videobroadcastEntity.stateParams,
      {
        onInitializedSuccess: () => {
          this.setReady(true);
        },
        onInitializationFailed: (_, error) => {
          this.setReady(false);

          console.error(error);
          this.notifications.showErrorMessageT('errors:failedToInitWidget');
        },
        onInternalStateChanged: (state) => {
          this.videobroadcastEntity.setStateParams(state);
        },
        onWellboreChanged: (wellboreName) => {
          this.videobroadcastEntity.setWellboreName(wellboreName);
        },
        onPlaybackTimeChanged: (timeMs, source) => {
          getLinkedVideoTimeCoordinator(this.groupSelectStore).publishFromVideo(
            this.videobroadcastEntity,
            timeMs,
            source,
          );
        },
        onVideoIntervalChanged: (interval) => {
          getLinkedVideoTimeCoordinator(this.groupSelectStore).publishIntervalChangeFromVideo(
            this.videobroadcastEntity,
            interval,
          );
        },
      },
    );
  }

  initWidgetByWellId(wellId: number) {
    const newState = this.getNewState(wellId);
    this.videobroadcastEntity.setState(newState);
  }

  effect = () => {
    this.setInActiveTab(true);
    const linkedVideoCoordinator = getLinkedVideoTimeCoordinator(this.groupSelectStore);

    const disposeGroupId = reaction(
      () => this.videobroadcastEntity.groupId,
      (groupId) => {
        if (groupId === null) return;

        const widgets = this.groupSelectStore.groupManager.getWidgetsByGroupId(groupId);
        setWellIdInGroup(widgets, this.videobroadcastEntity, this.wellListPanel);
      },
    );

    const disposeWellId = reaction(
      () => this.videobroadcastEntity.wellId,
      (wellId, prevWellId) => {
        if (wellId && !this.videobroadcastEntity.state) {
          const newState = this.getNewState(wellId);

          this.videobroadcastEntity.setState(newState);
        } else if (wellId && this.videobroadcastEntity.state && typeof prevWellId !== 'undefined') {
          if (this.videobroadcastEntity.stateParams.selectedCameras) {
            this.videobroadcastEntity.state.setSelectedCams(this.videobroadcastEntity.stateParams.selectedCameras);
          }

          this.videobroadcastEntity.state.setAppParams({
            wellId,
          });
        } else if (!wellId && this.videobroadcastEntity.state) {
          this.videobroadcastEntity.state.destroy();
          this.videobroadcastEntity.setState(null);
        }
      },
      { fireImmediately: true },
    );

    const disposeStateParams = reaction(
      () => ({
        theme: this.theme.theme,
        language: this.language.language,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
      }),
      ({ theme, language, isAltNames }) => {
        this.videobroadcastEntity.state?.setAppParams({
          theme,
          locale: getCorrectLocale(language),
          isAltNames,
        });
      },
      {
        fireImmediately: true,
      },
    );

    const wellListPanelDisposer = reaction(
      () => this.wellListPanel.store.selectedWellId,
      (wellId) => {
        if (this.wellListPanel.panelState?.groupId === this.videobroadcastEntity.groupId) {
          this.videobroadcastEntity.setWellId(wellId);
        }
      },
      {
        fireImmediately: true,
      },
    );

    const linkedVideoSyncDisposer = reaction(
      () => ({
        groupId: this.videobroadcastEntity.groupId,
        wellId: this.videobroadcastEntity.wellId,
        hasState: Boolean(this.videobroadcastEntity.state),
      }),
      ({ groupId, wellId }, previousState) => {
        if (previousState && (previousState.groupId !== groupId || previousState.wellId !== wellId)) {
          linkedVideoCoordinator.refreshVideoSyncForGroup(previousState.groupId, previousState.wellId);
        }

        linkedVideoCoordinator.refreshVideoSyncForWidget(this.videobroadcastEntity);
      },
      {
        fireImmediately: true,
        equals: comparer.structural,
      },
    );

    return () => {
      this.setInActiveTab(false);
      disposeGroupId();
      disposeWellId();
      disposeStateParams();
      wellListPanelDisposer();
      linkedVideoSyncDisposer();
    };
  };
}
